import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";

const DEFAULT_DEBOUNCE_MS = 1_000;

export const useFilaIntegracaoEvents = (
  lojaId?: number | null,
  options?: {
    enabled?: boolean;
    /** Só invalida keys da tela atual (evita cascade de ~18 GETs). */
    queryKeys: readonly string[];
    debounceMs?: number;
  }
) => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const enabledRef = useRef(options?.enabled !== false);
  enabledRef.current = options?.enabled !== false;
  const queryKeysRef = useRef(options?.queryKeys ?? []);
  queryKeysRef.current = options?.queryKeys ?? [];
  const debounceMsRef = useRef(options?.debounceMs ?? DEFAULT_DEBOUNCE_MS);
  debounceMsRef.current = options?.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const flushInvalidate = useCallback(() => {
    for (const key of queryKeysRef.current) {
      queryClient.invalidateQueries({
        queryKey: [key],
        refetchType: "active",
      });
    }
  }, [queryClient]);

  const scheduleInvalidate = useCallback(() => {
    if (debounceTimeoutRef.current != null) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      debounceTimeoutRef.current = null;
      flushInvalidate();
    }, debounceMsRef.current);
  }, [flushInvalidate]);

  const connect = useCallback(() => {
    if (!token || !enabledRef.current) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const params = new URLSearchParams();
    if (lojaId) {
      params.append("lojaId", lojaId.toString());
    }
    params.append("token", token);
    const url = `${apiUrl}/events/ranking/fila-integracao?${params.toString()}`;

    const eventSource = new EventSource(url, {
      withCredentials: false,
    });

    eventSource.onopen = () => {
      reconnectAttempts.current = 0;
    };

    eventSource.addEventListener("connected", () => {
      reconnectAttempts.current = 0;
    });

    eventSource.addEventListener("filaIntegracao.updated", () => {
      scheduleInvalidate();
    });

    eventSource.onerror = (error) => {
      console.error("SSE error for fila-integracao:", error);
      eventSource.close();

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay * reconnectAttempts.current);
      } else {
        console.error("Max reconnection attempts reached for fila-integracao");
      }
    };

    eventSourceRef.current = eventSource;
  }, [token, lojaId, scheduleInvalidate]);

  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      return;
    }

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, [connect, enabled]);
};
